import { Controller } from "@hotwired/stimulus"

// テーブル番号更新用のStimulusコントローラー
// 管理画面のメニュー詳細画面で使用
export default class extends Controller {
  static targets = ["input", "submitButton", "directButton", "message"]
  
  declare readonly inputTarget: HTMLInputElement
  declare readonly submitButtonTarget: HTMLButtonElement
  declare readonly directButtonTargets: HTMLButtonElement[]
  declare readonly hasMessageTarget: boolean
  declare readonly messageTarget: HTMLElement
  
  // データ属性からメニューIDを取得
  static values = {
    menuId: String,
    updateUrl: String
  }
  
  declare readonly menuIdValue: string
  declare readonly updateUrlValue: string
  
  // 初期値を保持（変更検知用）
  private originalValue: string = ""
  
  connect() {
    // 初期値を保持
    this.originalValue = this.inputTarget.value
    
    // 現在のテーブル番号に応じてボタンのスタイルを設定
    this.updateDirectButtonStyles()
  }
  
  // 入力フィールドの値が変更されたときの処理
  inputChanged() {
    // 値が変更されていれば送信ボタンを有効化
    const isChanged = this.inputTarget.value !== this.originalValue
    
    if (isChanged) {
      this.submitButtonTarget.classList.remove("uk-button-muted")
      this.submitButtonTarget.classList.add("uk-button-secondary")
    } else {
      this.submitButtonTarget.classList.remove("uk-button-secondary")
      this.submitButtonTarget.classList.add("uk-button-muted")
    }
  }
  
  // 直接番号ボタンがクリックされたときの処理
  directSelect(event: Event) {
    event.preventDefault()
    
    const button = event.currentTarget as HTMLButtonElement
    const tableNumber = button.textContent?.trim() || ""
    
    // 既にテーブル番号が設定されている場合は確認ダイアログを表示
    // originalValue（初期値）が空でない場合は既に設定済みと判断
    if (this.originalValue && this.originalValue !== "") {
      const currentNumber = this.originalValue
      const confirmMessage = `テーブル番号を「${currentNumber}」から「${tableNumber}」に変更しますか？`
      
      if (!confirm(confirmMessage)) {
        // キャンセルされた場合は何もしない
        return
      }
    }
    
    // 入力フィールドに値を設定
    this.inputTarget.value = tableNumber
    
    // 即座に更新を送信
    this.updateTableNumber(tableNumber)
  }
  
  // 変更を反映ボタンがクリックされたときの処理
  async submitChange(event: Event) {
    event.preventDefault()
    
    const tableNumber = this.inputTarget.value
    
    // 既にテーブル番号が設定されていて、新しい値が異なる場合は確認ダイアログを表示
    // originalValue（初期値）が空でなく、かつ新しい値と異なる場合
    if (this.originalValue && this.originalValue !== "" && this.originalValue !== tableNumber) {
      const currentNumber = this.originalValue
      const newNumber = tableNumber || "未設定"
      const confirmMessage = `テーブル番号を「${currentNumber}」から「${newNumber}」に変更しますか？`
      
      if (!confirm(confirmMessage)) {
        // キャンセルされた場合は元の値に戻す
        this.inputTarget.value = this.originalValue
        this.inputChanged() // ボタンのスタイルを更新
        return
      }
    }
    
    await this.updateTableNumber(tableNumber)
  }
  
  // テーブル番号をサーバーに送信して更新
  private async updateTableNumber(tableNumber: string) {
    // 送信中はボタンを無効化
    this.submitButtonTarget.disabled = true
    this.directButtonTargets.forEach(btn => btn.disabled = true)
    
    try {
      // CSRFトークンを取得
      const csrfToken = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content')
      
      const response = await fetch(this.updateUrlValue, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': csrfToken || '',
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          table_number: tableNumber === "" ? null : tableNumber
        })
      })
      
      const data = await response.json()
      
      if (response.ok && data.success) {
        // 成功時の処理
        this.originalValue = tableNumber
        this.inputTarget.value = tableNumber
        
        // ボタンスタイルをリセット
        this.submitButtonTarget.classList.remove("uk-button-secondary")
        this.submitButtonTarget.classList.add("uk-button-muted")
        
        // 直接番号ボタンのスタイルを更新
        this.updateDirectButtonStyles()
        
        // 成功メッセージを表示（メッセージターゲットがある場合）
        if (this.hasMessageTarget) {
          this.showMessage(data.message || 'テーブル番号を更新しました', 'success')
        }
      } else {
        // エラー時の処理
        console.error('Update failed:', data.errors)
        
        // エラーメッセージを表示
        if (this.hasMessageTarget) {
          const errorMessage = data.errors?.join(', ') || 'エラーが発生しました'
          this.showMessage(errorMessage, 'danger')
        }
        
        // 値を元に戻す
        this.inputTarget.value = this.originalValue
      }
    } catch (error) {
      console.error('Network error:', error)
      
      // ネットワークエラーメッセージを表示
      if (this.hasMessageTarget) {
        this.showMessage('通信エラーが発生しました', 'danger')
      }
      
      // 値を元に戻す  
      this.inputTarget.value = this.originalValue
    } finally {
      // ボタンを再度有効化
      this.submitButtonTarget.disabled = false
      this.directButtonTargets.forEach(btn => btn.disabled = false)
    }
  }
  
  // 直接番号ボタンのスタイルを更新
  private updateDirectButtonStyles() {
    const currentValue = this.inputTarget.value
    
    this.directButtonTargets.forEach(button => {
      const buttonValue = button.textContent?.trim() || ""
      
      if (buttonValue === currentValue) {
        // 現在選択されている番号のボタンをプライマリに
        button.classList.remove("uk-button-default")
        button.classList.add("uk-button-primary")
      } else {
        // それ以外はデフォルトスタイル
        button.classList.remove("uk-button-primary")
        button.classList.add("uk-button-default")
      }
    })
  }
  
  // メッセージを表示（UIKit notification使用）
  private showMessage(message: string, status: 'success' | 'danger') {
    if (typeof window.UIkit !== 'undefined') {
      window.UIkit.notification({
        message: message,
        status: status,
        pos: 'top-right',
        timeout: 3000
      })
    } else if (this.hasMessageTarget) {
      // UIKitが使えない場合は、メッセージターゲットに直接表示
      this.messageTarget.textContent = message
      this.messageTarget.className = `uk-alert uk-alert-${status}`
      
      // 3秒後に消す
      setTimeout(() => {
        this.messageTarget.textContent = ''
        this.messageTarget.className = ''
      }, 3000)
    }
  }
}